import { motion } from "framer-motion";
import { Shield, Rocket, CheckCircle, Target, Layers } from "lucide-react";

const PreuvesSection = () => {
  const approches = [
    {
      title: "Systèmes bancaires & finance",
      description: "Applications transactionnelles critiques soumises à forte contrainte réglementaire et sécuritaire.",
      capabilities: ["Conformité DSP2/RGPD", "Haute disponibilité", "Sécurité renforcée"],
      icon: <Shield className="w-5 h-5 text-lenvia-blue" />,
      accent: "from-blue-500/20 to-cyan-500/20"
    },
    {
      title: "Retail & e-commerce",
      description: "Plateformes haute performance conçues pour absorber des pics de charge massifs.",
      capabilities: ["Scalabilité dynamique", "Performance optimisée", "Résilience maximale"],
      icon: <Rocket className="w-5 h-5 text-lenvia-blue" />,
      accent: "from-violet-500/20 to-purple-500/20"
    },
    {
      title: "Modernisation SI",
      description: "Transformation progressive des systèmes legacy vers des architectures modernes et maintenables.",
      capabilities: ["Migration sécurisée", "Zéro interruption", "Architecture évolutive"],
      icon: <Layers className="w-5 h-5 text-lenvia-blue" />,
      accent: "from-emerald-500/20 to-teal-500/20"
    }
  ];

  return (
    <section id="preuves" className="section-spacing relative">
      {/* Background - LIGHTER */}
      <div className="absolute inset-0 bg-[#070B1A]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#070B1A] via-[#0A0F20]/50 to-[#070B1A]" />
      
      <div className="lenvia-container relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2.5 mb-6"
          >
            <Target className="w-4 h-4 text-lenvia-blue" />
            <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
              Notre approche
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-white mb-6"
          >
            Des solutions conçues pour{" "}
            <span className="gradient-text">vos environnements critiques</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-base lg:text-lg text-slate-400 max-w-2xl mx-auto"
          >
            Une expertise technique adaptée aux contraintes spécifiques de chaque secteur d'activité.
          </motion.p>
        </div>

        {/* Cards Grid - Premium design */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {approches.map((approche, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
              className="group relative"
            >
              <div className="glass-card p-8 lg:p-9 h-full flex flex-col">
                {/* Accent gradient on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${approche.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[1.25rem]`} />
                
                <div className="relative z-10 flex flex-col h-full">
                  {/* Header */}
                  <div className="flex items-start mb-7">
                    <div className="icon-container group-hover:scale-110 transition-transform duration-300">
                      {approche.icon}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-heading text-xl lg:text-[1.35rem] font-bold text-white mb-3 leading-snug">
                    {approche.title}
                  </h3>
                  <p className="text-[15px] text-slate-400 mb-7 leading-relaxed">
                    {approche.description}
                  </p>
                  
                  {/* Capabilities */}
                  <div className="mt-auto pt-6 border-t border-white/[0.06]">
                    <ul className="space-y-3.5">
                      {approche.capabilities.map((capability) => (
                        <li key={capability} className="flex items-center gap-3 text-sm">
                          <CheckCircle className="w-4 h-4 text-lenvia-success flex-shrink-0" />
                          <span className="text-slate-200 font-medium">{capability}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PreuvesSection;
